/**
 * Share for review.
 *
 * One link per project. Making a new one revokes the old, which is how an
 * author cuts off a link that has been forwarded further than they meant — so
 * the button says "Replace the link" once one exists, not "Create another".
 *
 * **The URL is shown exactly once.** Only the token's hash is stored, so this
 * dialog could not reproduce it later even if it wanted to; reopening shows the
 * link's *settings* and not its address. Cloud's share links work the same way,
 * and the reason is the same: a token that can be read back out of the server is
 * a token that leaks with a database dump.
 */

import { useCallback, useEffect, useState } from 'react';
import { api, ApiError, type LinkSummary, type ProjectWire, type ReviewerListWire } from '../api';
import { ds } from '../ds';
import { display, formatAgo, mono, monoPlain, RADIUS } from '../theme';
import { Avatar } from './rail/ThreadCard';

/** `2026-08-07` for the date input, from unix seconds. */
const toDateInput = (unix: number | null) =>
  unix ? new Date(unix * 1000).toISOString().slice(0, 10) : '';

export function ShareDialog({
  projectId,
  onClose
}: {
  projectId: string;
  onClose: () => void;
}) {
  const { Button, Icon } = ds();
  const [existing, setExisting] = useState<LinkSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [url, setUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [usePassword, setUsePassword] = useState(false);
  const [password, setPassword] = useState('');
  const [confidential, setConfidential] = useState(true);

  const [project, setProject] = useState<ProjectWire | null>(null);
  const [people, setPeople] = useState<ReviewerListWire | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteNote, setInviteNote] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);

  const refreshPeople = useCallback(() => {
    api.reviewers
      .list(projectId)
      .then(setPeople)
      .catch(() => undefined);
  }, [projectId]);

  useEffect(() => {
    api.links
      .get(projectId)
      .then((r) => {
        setExisting(r.link);
        if (r.link) {
          setUsePassword(r.link.hasPassword);
          setConfidential(r.link.confidential);
        }
      })
      .catch(() => setExisting(null))
      .finally(() => setLoading(false));
    api.projects
      .get(projectId)
      .then(setProject)
      .catch(() => undefined);
    refreshPeople();
  }, [projectId, refreshPeople]);

  /** Settings save as they change; the dialog has no Save button to forget. */
  const saveSettings = (input: {
    dueAt?: number | null;
    notifyCommentEmail?: boolean;
    notifyDueEmail?: boolean;
  }) => {
    setProject((p) =>
      p
        ? {
            ...p,
            ...(input.dueAt !== undefined ? { dueAt: input.dueAt } : {}),
            ...(input.notifyCommentEmail !== undefined
              ? { notifyCommentEmail: input.notifyCommentEmail }
              : {}),
            ...(input.notifyDueEmail !== undefined ? { notifyDueEmail: input.notifyDueEmail } : {})
          }
        : p
    );
    void api.settings.update(projectId, input).catch(() => undefined);
  };

  async function invite() {
    const address = inviteEmail.trim();
    if (!address || inviting) return;
    setInviting(true);
    setInviteNote(null);
    try {
      await api.invites.create(projectId, address);
      setInviteEmail('');
      setInviteNote('Invite sent.');
      refreshPeople();
    } catch (e) {
      setInviteNote(e instanceof ApiError ? e.message : 'That invite would not send.');
    } finally {
      setInviting(false);
    }
  }

  async function create() {
    setError(null);
    if (usePassword && password.trim().length < 6) {
      setError('A password needs at least six characters.');
      return;
    }
    try {
      const res = await api.links.create(projectId, {
        password: usePassword ? password.trim() : undefined,
        confidential
      });
      setUrl(`${window.location.origin}${res.url}`);
      setExisting(await api.links.get(projectId).then((r) => r.link));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'That link could not be made.');
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Share for review"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
      className="mv-scrim"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(8,6,16,.6)',
        display: 'grid',
        placeItems: 'center',
        padding: 24,
        zIndex: 60
      }}
    >
      <div
        className="mv-pop"
        style={{
          width: 'min(540px, 100%)',
          background: 'var(--surface-card)',
          borderRadius: RADIUS.panel,
          padding: '28px 30px 24px',
          boxShadow: '0 32px 70px rgba(8,6,16,.6)'
        }}
      >
        <p style={mono}>Share for review</p>
        <h2 style={{ ...display(24), marginTop: 8 }}>Send this to whoever needs to comment.</h2>

        {url ? (
          <>
            <div
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                marginTop: 16,
                padding: '10px 12px',
                borderRadius: RADIUS.control,
                border: '1px solid var(--border-subtle)',
                background: 'var(--surface-sunken, transparent)'
              }}
            >
              <span
                style={{
                  ...monoPlain,
                  flex: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  color: 'var(--text-strong)'
                }}
              >
                {url}
              </span>
              <Button
                onClick={() => {
                  void navigator.clipboard.writeText(url).then(() => setCopied(true));
                }}
              >
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </div>
            {/* Said here because this is the only moment it is true. */}
            <p style={{ color: 'var(--text-body)', marginTop: 12, lineHeight: 1.6 }}>
              Copy this now — it is not shown again. Only a hash of it is stored, so nobody,
              including us, can read it back out later.
            </p>
            {usePassword && (
              <p style={{ ...monoPlain, marginTop: 8 }}>
                The password is not in the link. Send it separately.
              </p>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
              <Button onClick={onClose}>Done</Button>
            </div>
          </>
        ) : (
          <>
            <p style={{ color: 'var(--text-body)', marginTop: 12, lineHeight: 1.6 }}>
              Anyone with the link can read the course and comment on it. No Mauvely account —
              they give a name the first time they post.
            </p>

            {existing && (
              <p style={{ ...monoPlain, marginTop: 12 }}>
                <Icon name="link-2" size={12} /> A link already exists · opened{' '}
                {existing.viewCount} time{existing.viewCount === 1 ? '' : 's'}
                {existing.hasPassword ? ' · password on' : ''}
              </p>
            )}

            <Toggle
              on={confidential}
              onChange={setConfidential}
              title="Confidentiality notice"
              desc="A short acknowledgement before the course opens. A speed-bump, not a lock."
            />

            <Toggle
              on={usePassword}
              onChange={setUsePassword}
              title="Password on the link"
              desc="Reviewers enter it before the course opens. Ten guesses an hour, then it stops."
            />

            {usePassword && (
              <input
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password for this link"
                style={{
                  width: '100%',
                  marginTop: 8,
                  padding: '9px 12px',
                  borderRadius: RADIUS.control,
                  border: '1px solid var(--border-strong, var(--border-subtle))',
                  background: 'var(--surface-sunken, transparent)',
                  color: 'var(--text-strong)',
                  font: 'inherit',
                  fontSize: 14
                }}
              />
            )}

            <label style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16 }}>
              <span style={{ flex: 1 }}>
                <span style={{ display: 'block', color: 'var(--text-strong)', fontSize: 14 }}>
                  Comments due
                </span>
                <span style={{ display: 'block', color: 'var(--text-muted)', fontSize: 13, marginTop: 2 }}>
                  Shown to reviewers on the course.
                </span>
              </span>
              <input
                type="date"
                value={toDateInput(project?.dueAt ?? null)}
                onChange={(e) => {
                  const v = e.target.value;
                  saveSettings({ dueAt: v ? Math.floor(new Date(`${v}T12:00:00Z`).getTime() / 1000) : null });
                }}
                style={{
                  padding: '7px 10px',
                  borderRadius: RADIUS.control,
                  border: '1px solid var(--border-strong, var(--border-subtle))',
                  background: 'var(--surface-sunken, transparent)',
                  color: 'var(--text-strong)',
                  font: 'inherit',
                  fontSize: 13
                }}
              />
            </label>

            <Toggle
              on={project?.notifyCommentEmail ?? true}
              onChange={(v) => saveSettings({ notifyCommentEmail: v })}
              title="Email me on every new comment"
              desc="Comments still arrive here live, whether this is on or off."
            />

            <Toggle
              on={project?.notifyDueEmail ?? true}
              onChange={(v) => saveSettings({ notifyDueEmail: v })}
              title="Remind reviewers before the due date"
              desc="Sent 2 days out, only to reviewers who haven't commented."
            />

            {people && (people.reviewers.length > 0 || people.invites.length > 0) && (
              <div style={{ marginTop: 20 }}>
                <p style={mono}>Reviewers · round {project?.round ?? 1}</p>
                {people.reviewers.map((r) => (
                  <div
                    key={r.id}
                    style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '7px 0' }}
                  >
                    <Avatar name={r.name} colour={r.colour} size={20} />
                    <span style={{ minWidth: 0, flex: 1 }}>
                      <span
                        style={{
                          display: 'block',
                          fontSize: 13,
                          color: 'var(--text-strong)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {r.name}
                      </span>
                      <span style={{ ...monoPlain, fontSize: 8.5 }}>{r.email}</span>
                    </span>
                    {r.approvedAt && (
                      <span style={{ ...mono, fontSize: 8, color: 'var(--signal)' }}>APPROVED</span>
                    )}
                    <span
                      style={{
                        ...mono,
                        fontSize: 8,
                        whiteSpace: 'nowrap',
                        color: r.comments > 0 ? 'var(--signal)' : '#e8a33d'
                      }}
                    >
                      {r.comments > 0
                        ? `COMMENTED · ${formatAgo(r.commentedAt!).toUpperCase()}`
                        : 'OPENED, NOT COMMENTED'}
                    </span>
                  </div>
                ))}
                {people.invites.map((i) => (
                  <div
                    key={i.id}
                    style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '7px 0' }}
                  >
                    <span
                      aria-hidden="true"
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: 999,
                        border: '1px dashed var(--border-strong, var(--border-subtle))',
                        flex: 'none'
                      }}
                    />
                    <span style={{ minWidth: 0, flex: 1, fontSize: 13, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {i.email}
                    </span>
                    <span style={{ ...mono, fontSize: 8, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                      {i.openedAt ? 'INVITED, OPENED' : 'INVITED, NOT OPENED'}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        void api.invites.revoke(projectId, i.id).then(refreshPeople, () => undefined);
                      }}
                      style={{ ...mono, fontSize: 8.5, background: 'none', border: 0, color: 'var(--danger-500)', cursor: 'pointer' }}
                    >
                      Revoke
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <input
                value={inviteEmail}
                type="email"
                onChange={(e) => setInviteEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && void invite()}
                placeholder="name@company.com"
                style={{
                  flex: 1,
                  padding: '9px 12px',
                  borderRadius: RADIUS.control,
                  border: '1px solid var(--border-strong, var(--border-subtle))',
                  background: 'var(--surface-sunken, transparent)',
                  color: 'var(--text-strong)',
                  font: 'inherit',
                  fontSize: 13.5
                }}
              />
              <Button onClick={() => void invite()} disabled={inviting || !inviteEmail.trim()}>
                Invite by email
              </Button>
            </div>
            {inviteNote && (
              <p style={{ ...monoPlain, marginTop: 8 }}>{inviteNote}</p>
            )}

            {error && <p style={{ color: 'var(--danger-500)', marginTop: 12 }}>{error}</p>}

            <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
              <Button variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={() => void create()} disabled={loading}>
                {existing ? 'Replace the link' : 'Create the link'}
              </Button>
            </div>

            {existing && (
              <p style={{ ...monoPlain, marginTop: 10, textAlign: 'right' }}>
                Replacing stops the old link working straight away.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Toggle({
  on,
  onChange,
  title,
  desc
}: {
  on: boolean;
  onChange: (v: boolean) => void;
  title: string;
  desc: string;
}) {
  return (
    <label
      style={{
        display: 'flex',
        gap: 12,
        alignItems: 'flex-start',
        marginTop: 16,
        cursor: 'pointer'
      }}
    >
      <input
        type="checkbox"
        checked={on}
        onChange={(e) => onChange(e.target.checked)}
        style={{ marginTop: 3 }}
      />
      <span>
        <span style={{ display: 'block', color: 'var(--text-strong)', fontSize: 14 }}>{title}</span>
        <span style={{ display: 'block', color: 'var(--text-muted)', fontSize: 13, marginTop: 2 }}>
          {desc}
        </span>
      </span>
    </label>
  );
}
